// helpers/postDisplay.js
//
// Single source of truth for how a Post is rendered across the three
// view tabs (Calendar / List / Kanban). Before this file, each view
// formatted dates with its own toLocaleString call + maintained its
// own (wrong) platform-color map, so the same post showed different
// times and different icon colors depending on the tab.
//
// Everything here is pure. No redux, no API. Used by:
//   - SocialMediaBrand.jsx  (Calendar view)
//   - PostListView.jsx      (List view)
//   - KanbanBoard/KanbanCard.jsx
//   - CreatePostModal.jsx

import React, { useState } from 'react';
import { hasFailureDetails } from './postFailures';
import { canUnpublish } from './postUnpublishable';

// Lazy-load PostFailureModal to break the circular dep:
// postDisplay → PostFailureModal → postDisplay (for PlatformIcon).
// Eager-importing put `PlatformIcon` as undefined when the modal
// module evaluated first, which crashed every page that transitively
// imports postDisplay (configuration/brandSettings.jsx → composer →
// helpers/postDisplay) → white screen.
let _PostFailureModal = null;
function loadPostFailureModal() {
  if (_PostFailureModal) return _PostFailureModal;
  // require() resolves at call-time (after both modules have finished
  // evaluating), so PlatformIcon is fully bound by then.
  _PostFailureModal = require('../components/PostFailureModal/PostFailureModal').default;
  return _PostFailureModal;
}

// Canonical brand colors. The MDB "*-text" classes shipped with
// inaccurate values (Twitter on info-text was teal not Twitter blue,
// Instagram on pink-text dropped the gradient, TikTok on dark-text
// blended into the background). We render inline so the actual brand
// color always wins, regardless of theme.
export const PLATFORM_COLORS = {
  linkedin:  '#0A66C2',
  twitter:   '#1DA1F2',
  x:         '#000000',   // Twitter rebrand alias
  facebook:  '#1877F2',
  instagram: '#E4405F',   // representative; gradient is below
  youtube:   '#FF0000',
  tiktok:    '#000000',
  pinterest: '#E60023',
  snapchat:  '#FFFC00',
  google:    '#4285F4',
  threads:   '#000000',
  whatsapp:  '#25D366',
};

// Instagram is the only one with a brand gradient — used as
// `background: PLATFORM_GRADIENT.instagram` on the icon's container
// when we want the full effect (calendar + composer). Single color
// in PLATFORM_COLORS is the fallback for places where solid is fine.
export const PLATFORM_GRADIENT = {
  instagram: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
};

// Font Awesome icon class for each platform. Matches the icons the
// post composer (CreatePostModal's accountsRedux entries) uses on its
// toggle rail, so the same platform looks identical on every surface —
// composer, calendar, kanban, list. The fully-rounded badges
// (`fab fa-linkedin` not `-in`, `fab fa-pinterest` not `-p`,
// `fab fa-snapchat` not `-ghost`) match the composer's design.
export const PLATFORM_ICONS = {
  linkedin:  'fab fa-linkedin',
  twitter:   'fab fa-twitter',
  x:         'fab fa-twitter',
  facebook:  'fab fa-facebook',
  instagram: 'fab fa-instagram',
  youtube:   'fab fa-youtube',
  tiktok:    'fab fa-tiktok',
  pinterest: 'fab fa-pinterest',
  snapchat:  'fab fa-snapchat',
  google:    'fab fa-google',
  threads:   'fab fa-threads',
  whatsapp:  'fab fa-whatsapp',
};

export function colorFor(platform) {
  if (!platform) return '#525252';
  return PLATFORM_COLORS[String(platform).toLowerCase()] || '#525252';
}

export function iconFor(platform) {
  if (!platform) return 'fab fa-share-alt';
  return PLATFORM_ICONS[String(platform).toLowerCase()] || 'fa fa-share-alt';
}

// Inline-styled platform icon. Used everywhere a small color-coded
// icon represents a target platform. `active` controls whether the
// icon shows in full color or muted (for the composer's "click to
// toggle" rail; calendar/kanban always pass active=true).
export function PlatformIcon({ platform, active = true, size = 16, withBg = false, title }) {
  if (!platform) return null;
  const p = String(platform).toLowerCase();
  const color = colorFor(p);
  const icon = iconFor(p);
  const gradient = PLATFORM_GRADIENT[p];

  if (withBg) {
    // Pill background for the composer's network rail.
    const bg = !active
      ? '#f3f4f6'
      : (gradient || color);
    return (
      <span
        title={title || platform}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: size * 2.25, height: size * 2.25, borderRadius: '50%',
          background: bg,
          color: active ? '#fff' : '#9ca3af',
          margin: '0 4px',
          fontSize: size,
          cursor: 'pointer',
          opacity: active ? 1 : 0.6,
          transition: 'all 0.15s ease',
        }}>
        <i className={icon} />
      </span>
    );
  }

  return (
    <i
      className={icon}
      title={title || platform}
      style={{
        color: active ? color : '#9ca3af',
        fontSize: size,
        marginRight: 4,
        opacity: active ? 1 : 0.5,
      }} />
  );
}

// Canonical time formatter. ALL post views call this so kanban + list
// + calendar render identical strings for the same scheduled_at.
// Uses Intl directly (no moment) — the user's browser locale + tz
// drive the output. Format is short month, day, year (when not
// current year), then HH:MM in 12-hour. Example: "May 13, 2026 · 5:42 PM".
export function formatPostTime(input) {
  if (!input) return '—';
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return '—';
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  const dateStr = d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  const timeStr = d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${dateStr} · ${timeStr}`;
}

// Short date-only for compact spots (calendar cells, list pagination).
export function formatPostDate(input) {
  if (!input) return '—';
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Normalize a stored media value into something <img> / <video> can
// render. Three shapes coexist in the DB:
//   1. Full URL — return as-is.
//   2. Bare key ending in .gz — legacy zip-wrapped storage, needs
//      extractImageFromZip to resolve. Marked needsUnzip:true so
//      <PostThumb> can do it lazily.
//   3. Bare key with a regular extension (.png, .jpg, .mp4 …) — the
//      new no-zip post-media path. Prepend /uploads/ so the express
//      static middleware (or R2 redirect) serves it.
export function normalizeMediaUrl(s) {
  if (!s || typeof s !== 'string') return { url: null, needsUnzip: false };
  if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('blob:') || s.startsWith('data:')) {
    return { url: s, needsUnzip: false };
  }
  if (s.endsWith('.gz')) {
    // Legacy zip-wrapped storage. Pass the bare key through; the
    // <PostThumb> component will unzip it via extractImageFromZip.
    return { url: s, needsUnzip: true };
  }
  const path = s.startsWith('/uploads/') ? s
            : s.startsWith('uploads/') ? `/${s}`
            : `/uploads/${s}`;
  return { url: path, needsUnzip: false };
}

// Pick the first usable thumbnail from a post. Posts store
// imageContent: string[] and videoContent: string[]; we accept legacy
// shapes (image, images[].url) as fallbacks. Returns { url, isVideo,
// needsUnzip } or null if no media.
export function pickPostThumb(post = {}) {
  let raw = null;
  let isVideo = false;
  if (Array.isArray(post.imageContent) && post.imageContent[0]) {
    raw = post.imageContent[0];
  } else if (Array.isArray(post.images) && post.images[0]) {
    raw = post.images[0].url || post.images[0];
  } else if (post.image) {
    raw = post.image.url || post.image;
  } else if (Array.isArray(post.videoContent) && post.videoContent[0]) {
    raw = post.videoContent[0];
    isVideo = true;
  }
  if (!raw) return null;
  const { url, needsUnzip } = normalizeMediaUrl(raw);
  if (!url) return null;
  return { url, isVideo, needsUnzip };
}

// Renders a post's first media as an <img> or <video>. Handles all
// three storage shapes (see normalizeMediaUrl). For legacy .gz keys it
// lazily fetches + unzips via extractImageFromZip and updates state
// once the blob URL is ready. Renders nothing while loading.
export function PostThumb({ post, style = {}, alt = '', className = '' }) {
  const thumb = pickPostThumb(post);
  const [resolved, setResolved] = React.useState(thumb && !thumb.needsUnzip ? thumb.url : null);

  React.useEffect(() => {
    let cancelled = false;
    if (!thumb) { setResolved(null); return; }
    if (!thumb.needsUnzip) { setResolved(thumb.url); return; }
    (async () => {
      try {
        // Imported lazily — the helper pulls in JSZip which is heavy.
        const mod = await import('./extractImageFromZip');
        const out = await mod.extractImageFromZip(thumb.url);
        if (!cancelled) setResolved(out || null);
      } catch {
        if (!cancelled) setResolved(null);
      }
    })();
    return () => { cancelled = true; };
  }, [thumb && thumb.url, thumb && thumb.needsUnzip]);

  if (!thumb || !resolved) return null;
  if (thumb.isVideo) {
    return <video src={resolved} className={className} style={style} muted />;
  }
  return <img src={resolved} alt={alt} className={className} style={style}
              onError={(e) => { e.currentTarget.style.display = 'none'; }} />;
}

// Lifecycle status display. Post.queueStatus / Idea.queueStatus are
// the source of truth (enum on the model). Calendar + List used to
// read post.statusKey — a phantom field that never existed — which
// is why every post showed "pending" forever. This helper centralises
// the queueStatus → { label, color, bg } mapping so the three view
// tabs render the SAME state for the SAME item. Kanban uses a
// separate column-bucketing helper (kanbanBucket.js); the labels
// here mirror those buckets so the user sees consistent words.
const POST_STATUS_DISPLAY = {
  draft:                       { label: 'Draft',                 color: '#525252', bg: '#f3f4f6' },
  pending:                     { label: 'Pending',               color: '#856404', bg: '#fff4cc' },
  'generating-image':          { label: 'Generating image…',     color: '#856404', bg: '#fff4cc' },
  'pending-content-approval':  { label: 'Awaiting approval',     color: '#856404', bg: '#fff4cc' },
  'awaiting-approval':         { label: 'Awaiting approval',     color: '#856404', bg: '#fff4cc' },
  approved:                    { label: 'Approved',              color: '#1e6b3e', bg: '#e7f5ec' },
  scheduled:                   { label: 'Scheduled',             color: '#15172b', bg: '#eef2ff' },
  posting:                     { label: 'Posting…',              color: '#0F62FE', bg: '#e0eaff' },
  posted:                      { label: 'Posted',                color: '#fff',    bg: '#1e6b3e' },
  'partially-posted':          { label: 'Partially posted',      color: '#856404', bg: '#fff4cc' },
  rejected:                    { label: 'Rejected',              color: '#a71d2a', bg: '#fdecea' },
  'image-generation-failed':   { label: 'Image gen failed',      color: '#a71d2a', bg: '#fdecea' },
  'email-failed':              { label: 'Approval email failed', color: '#a71d2a', bg: '#fdecea' },
  'posting-failed':            { label: 'Posting failed',        color: '#a71d2a', bg: '#fdecea' },
  archived:                    { label: 'Archived',              color: '#fff',    bg: '#7c3aed' },
};
const IDEA_STATUS_DISPLAY = {
  draft:               { label: 'Draft',             color: '#525252', bg: '#f3f4f6' },
  pending:             { label: 'Pending',           color: '#856404', bg: '#fff4cc' },
  generating:          { label: 'Generating…',       color: '#856404', bg: '#fff4cc' },
  'pending-approval':  { label: 'Awaiting approval', color: '#856404', bg: '#fff4cc' },
  'awaiting-approval': { label: 'Awaiting approval', color: '#856404', bg: '#fff4cc' },
  approved:            { label: 'Approved',          color: '#1e6b3e', bg: '#e7f5ec' },
  rejected:            { label: 'Rejected',          color: '#a71d2a', bg: '#fdecea' },
  posted:              { label: 'Used',              color: '#fff',    bg: '#1e6b3e' },
  failed:              { label: 'Failed',            color: '#a71d2a', bg: '#fdecea' },
  'email-failed':      { label: 'Approval email failed', color: '#a71d2a', bg: '#fdecea' },
};

// Read the canonical lifecycle string off an item — queueStatus
// preferred, then plain status string, then nested status.key. This
// is the same priority order kanbanBucket.js uses, so the three
// views agree on the source field.
export function rawStatus(item) {
  if (!item || typeof item !== 'object') return null;
  if (typeof item.queueStatus === 'string' && item.queueStatus) return item.queueStatus;
  if (typeof item.status === 'string' && item.status) return item.status;
  if (item.status && typeof item.status === 'object') {
    return item.status.key || item.status.name || null;
  }
  return null;
}

export function displayStatus(item, source = 'post') {
  const raw = rawStatus(item);
  const table = source === 'idea' ? IDEA_STATUS_DISPLAY : POST_STATUS_DISPLAY;
  if (raw && table[raw]) return { ...table[raw], raw };
  return { label: raw || 'Unknown', color: '#525252', bg: '#f3f4f6', raw: raw || null };
}

// Inline-styled status pill. Replaces the old hard-coded MDBBadge in
// SocialMediaBrand + PostListView. Same component used in both
// places so calendar + list pills look identical.
//
// For posts whose lifecycle ended in a failure state, the pill turns
// into a clickable affordance that opens a popup with the per-platform
// failure reasons + a "Retry all platforms" button. Self-contained
// (no parent wiring) so kanban / list / calendar all get the behavior
// for free — wherever StatusBadge appears, it Just Works.
export function StatusBadge({ item, source = 'post', size = 11 }) {
  const s = displayStatus(item, source);
  const [open, setOpen] = useState(false);
  // Only attach the click handler when there's actually something to
  // show — failure details to inspect, OR live platforms the operator can
  // take down. Keeps the visual identity for normal lifecycle states.
  const isClickable = source === 'post' && (hasFailureDetails(item) || canUnpublish(item));

  return (
    <>
      <span
        title={isClickable
          ? `${s.raw}: click for details / take-down options`
          : (s.raw ? `Lifecycle: ${s.raw}` : 'No status')}
        onClick={isClickable ? (e) => { e.stopPropagation(); setOpen(true); } : undefined}
        style={{
          display: 'inline-block', padding: '2px 8px', borderRadius: 10,
          background: s.bg, color: s.color, fontSize: size, fontWeight: 600,
          whiteSpace: 'nowrap',
          cursor: isClickable ? 'pointer' : 'default',
          textDecoration: isClickable ? 'underline dotted' : 'none',
        }}>
        {s.label}
      </span>
      {isClickable && (() => {
        const Modal = loadPostFailureModal();
        return Modal ? (
          <Modal
            isOpen={open}
            toggle={() => setOpen((v) => !v)}
            post={item}
          />
        ) : null;
      })()}
    </>
  );
}

// Publication-type indicator. A post's "type" (text / text-image /
// text-video / text-image-video) controls which surfaces it can land
// on — image-only posts skip text-only channels, etc. We render this
// as a small composite icon with a tooltip on every card view so the
// operator can scan the queue and know at a glance what the post
// will look like when published.
const TYPE_LABEL = {
  text:             'Text only',
  'text-image':     'Text + image',
  'text-video':     'Text + video',
  'text-image-video': 'Text + image + video',
  image:            'Image only',
  video:            'Video only',
};

export function pubTypeLabel(type) {
  if (!type) return 'Untyped';
  return TYPE_LABEL[String(type).toLowerCase()] || String(type);
}

// Returns array of icon class strings to render in sequence.
// e.g. text-image → ['fa fa-font', 'fa fa-image']
export function pubTypeIcons(type) {
  const t = String(type || '').toLowerCase();
  const out = [];
  if (t.includes('text') || t === '') out.push('fa fa-font');
  if (t.includes('image')) out.push('fa fa-image');
  if (t.includes('video')) out.push('fa fa-video');
  if (!out.length) out.push('fa fa-file-alt');
  return out;
}

// Stylable composite badge for a post's publication type. Tooltip
// carries the full label so the operator sees "Text + image" on hover
// without taking screen real estate.
export function PubTypeBadge({ type, size = 11 }) {
  const icons = pubTypeIcons(type);
  return (
    <span
      title={pubTypeLabel(type)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        padding: '2px 6px', borderRadius: 10,
        background: '#f3f4f6', color: '#374151', fontSize: size,
        whiteSpace: 'nowrap',
      }}>
      {icons.map((cls, i) => <i key={i} className={cls} style={{ fontSize: size }} />)}
    </span>
  );
}

// Resolve the targeted platforms array on a post into a unique list
// of lowercase platform strings, regardless of which legacy shape the
// post uses (socialPlatforms, targetedSocialAccounts).
export function postPlatforms(post = {}) {
  const out = new Set();
  if (Array.isArray(post.socialPlatforms)) {
    for (const p of post.socialPlatforms) {
      if (typeof p === 'string') out.add(p.toLowerCase());
      else if (p && p.platform) out.add(String(p.platform).toLowerCase());
    }
  }
  if (Array.isArray(post.targetedSocialAccounts)) {
    for (const t of post.targetedSocialAccounts) {
      if (t && t.platform) out.add(String(t.platform).toLowerCase());
    }
  }
  return Array.from(out);
}

// Extract the SocialAccount _id strings a post targets, robust to BOTH
// shapes a post can carry:
//   - populated account objects ({ _id, platform, ... }) — GET / POST /posts
//   - bare ObjectId / id strings                          — PUT /posts
// PUT /posts doesn't populate its response, and the UPDATE_POST reducer
// swaps the post's redux copy for that response — so once a post has been
// edited+saved its targetedSocialAccounts collapse to bare id strings.
// The composer seeds its "targeted platforms" toggle state from this list;
// reading `x._id` blindly turned every bare string into `undefined`, which
// is exactly why clicking a connected network to enable publishing in the
// Update Post modal stopped persisting after the first schedule/save.
// Normalises to non-empty trimmed strings and drops blanks/dupes.
export function targetedAccountIds(post = {}) {
  const raw = Array.isArray(post.targetedSocialAccounts) ? post.targetedSocialAccounts : [];
  const out = [];
  const seen = new Set();
  for (const t of raw) {
    if (t == null) continue;
    // Populated object → its _id (id fallback); bare string → itself.
    const id = typeof t === 'object' ? (t._id ?? t.id) : t;
    if (id == null) continue;
    const s = String(id).trim();
    if (s && !seen.has(s)) { seen.add(s); out.push(s); }
  }
  return out;
}

// Resolve the platform-slug "selection stack" to pre-tick in the composer's
// social-network toggle bar when EDITING a post. Must agree with the targeted
// account ids seeded from targetedAccountIds(): a tile is active iff the post
// targets that platform's account.
//
// Why this exists separately from postPlatforms(): once a post has been
// edited+saved, PUT /posts returns its targetedSocialAccounts as BARE id
// strings (no `.platform`), so postPlatforms() yields [] and the toggle bar
// renders every network inactive even though the post still targets them.
// The user then sees an empty selection, and clicking a network they thought
// was already selected silently REMOVES its id from the stack (because the id
// was seeded but the slug wasn't) — the exact desync this ticket reports.
//
// Fix: union the populated-object platforms (postPlatforms) with the platform
// slugs resolved by looking each targeted account id up in the brand's known
// accounts (which always carry both _id and platform). Result is deduped,
// lowercased slugs that stay in lock-step with targetedAccountIds().
export function selectedPlatformsForPost(post = {}, accounts = []) {
  const norm = (p) => String(p || '').toLowerCase().trim();
  const byId = new Map();
  for (const a of Array.isArray(accounts) ? accounts : []) {
    if (a && a._id != null && a.platform) byId.set(String(a._id), norm(a.platform));
  }
  const out = new Set(postPlatforms(post));
  for (const id of targetedAccountIds(post)) {
    const pf = byId.get(String(id));
    if (pf) out.add(pf);
  }
  return Array.from(out);
}

// Resolve the SocialAccount _id strings to actually schedule a post against,
// derived from the SAME data the composer uses to render a network tile as
// "active": the resolved connectedAccounts list + the selectedPlatforms slug
// stack. This is the single source of truth that keeps "what the tiles show"
// and "what gets saved as targetedSocialAccounts" in lock-step.
//
// Why this exists: the composer used to validate/submit a SEPARATE
// `targetedPlatforms` (account-id) state that was seeded from the post's
// targetedSocialAccounts, while the tiles lit up from `selectedPlatforms`
// (slugs) seeded — via selectedPlatformsForPost — from the post's
// socialPlatforms too. A post carrying platform slugs but no resolvable
// targeted-account id therefore rendered tiles ACTIVE yet failed to schedule
// with "no social media selected". Deriving the ids from the active tiles
// removes that whole desync class. Only active tiles with a real account _id
// are returned; matching is case-insensitive and the result is deduped.
export function resolveTargetedAccountIds(connectedAccounts = [], selectedPlatforms = []) {
  const norm = (p) => String(p || '').toLowerCase().trim();
  const selected = new Set((Array.isArray(selectedPlatforms) ? selectedPlatforms : []).map(norm));
  const out = [];
  const seen = new Set();
  for (const a of Array.isArray(connectedAccounts) ? connectedAccounts : []) {
    if (!a || a.status !== 'active' || a._id == null) continue;
    if (!selected.has(norm(a.platform))) continue;
    const idStr = String(a._id).trim();
    if (idStr && !seen.has(idStr)) { seen.add(idStr); out.push(idStr); }
  }
  return out;
}

// Union a brand's connected social accounts from several sources (deduped),
// keeping the freshest/connected entry for each.
//
// Why: the composer reads the brand's accounts from BOTH `post.selectedBrand`
// (loaded once when the brand is opened) and the live `user.brands` tree (kept
// up to date as platforms are connected via OAuth). Preferring only the first
// source meant a platform connected AFTER the brand was opened never appeared,
// so the user couldn't add it to a post in the preview. Merging both sources
// guarantees a newly connected platform always surfaces. Dedup key is the
// account _id when present, else the platform; a `connected` entry always wins
// over a non-connected one for the same key.
export function mergeBrandSocialAccounts(...lists) {
  const norm = (p) => String(p || '').toLowerCase().trim();
  const byKey = new Map();
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const a of list) {
      if (!a) continue;
      const key = a._id ? `id:${String(a._id)}` : `pf:${norm(a.platform)}`;
      const existing = byKey.get(key);
      if (!existing) { byKey.set(key, a); continue; }
      // Upgrade to a connected entry if the one we kept wasn't connected.
      if (existing.status !== 'connected' && a.status === 'connected') byKey.set(key, a);
    }
  }
  return Array.from(byKey.values());
}

// Resolve which of the modal's base platform tiles are AVAILABLE (the
// brand has a connected account for them) and enrich the tile with that
// account's real profile data + _id.
//
// An account counts as available when it's a connected entry on the
// brand OR it's already targeted by the post being edited (a post can
// only target a connected account, so its targeted set is connected by
// definition — this is the fallback that keeps the tiles live even when
// the modal is opened without the brand's socialAccounts loaded, which
// was making every tile grey out). Platform match is case-insensitive.
export function resolveConnectedSocialAccounts({
  baseAccounts = [],
  brandSocialAccounts = [],
  postTargetedAccounts = [],
} = {}) {
  const norm = (p) => String(p || '').toLowerCase().trim();
  const available = new Map();
  // Post's targeted accounts first; richer brand entries overwrite them.
  for (const t of postTargetedAccounts) {
    if (t && t.platform) available.set(norm(t.platform), t);
  }
  for (const a of brandSocialAccounts) {
    if (a && a.platform && a.status === 'connected') available.set(norm(a.platform), a);
  }
  return baseAccounts.map((x) => {
    const match = available.get(norm(x.platform));
    if (!match) return { ...x, status: 'inactive' };
    return {
      ...x,
      status: 'active',
      _id: match._id,
      user_id: match.user_id,
      accountName: match.accountName || match.title,
      bio: match.description,
      avatar: match.avatar,
      handle: match.socialHandle,
      heading: match.socialHeading,
      verified: match.verified,
    };
  });
}
